// Angular
import {
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
// PrimeNG
import { ButtonModule } from 'primeng/button';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SidebarModule } from 'primeng/sidebar';
import { DropdownChangeEvent, DropdownModule } from 'primeng/dropdown';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
// Models
import { Store } from '@core/services-v2/geolocation/models/store.interface';
import { ControlsOf } from '@shared/models-v3/controls-of.type';
// Components
import { MapComponentV2 } from '@shared/components-v3/blocks/map-v2/map-v2.component';

const NG_MODULES = [ReactiveFormsModule];
const PRIME_MODULES = [
  SidebarModule,
  ButtonModule,
  RadioButtonModule,
  DropdownModule,
  ConfirmDialogModule,
];
const COMPONENTS = [MapComponentV2];
@Component({
  selector: 'app-store-list-sidebar',
  standalone: true,
  imports: [NG_MODULES, PRIME_MODULES, COMPONENTS],
  templateUrl: './store-list-sidebar.component.html',
  styleUrl: './store-list-sidebar.component.scss',
  providers: [ConfirmationService],
})
export class StoreListSidebarComponent {
  //#region ViewChild Components
  @ViewChild(MapComponentV2) mapComponent!: MapComponentV2;
  //#endregion

  //#region Inputs & Outputs
  @Input() stores: Store[] = [];
  @Input() selectedStoreCode: string | null = null;
  @Output() onSubmit: EventEmitter<Store> = new EventEmitter();
  //#endregion

  storeForm!: FormGroup<ControlsOf<{ code: string }>>;
  pendingMarkerUpdate = signal<boolean>(false);
  isMapReady = signal<boolean>(false);
  isShowedSelectStoreObservation = signal<boolean>(false);

  visible: boolean = false;
  selectedStore: Store | null = null;

  //#region Getters
  get codeField(): FormControl<string> {
    return this.storeForm.controls.code;
  }
  //#endregion

  //#region Dependencies
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly confirmationService = inject(ConfirmationService);
  //#endregion

  constructor() {
    this.buildForm();
  }

  onMapReady(): void {
    this.isMapReady.set(true);
    if (this.pendingMarkerUpdate()) {
      this.setSelectedStore(this.selectedStoreCode);
    }
  }

  private buildForm(): void {
    this.storeForm = this.fb.group({
      code: ['', Validators.required],
    });
  }

  /**
   * Mostrar sidebar.
   * - Este método se llama desde el componente padre.
   */
  show(): void {
    this.visible = true;
  }

  onShow(): void {
    this.isMapReady.set(false);
    this.setSelectedStore(this.selectedStoreCode);
  }

  private setSelectedStore(selectedStoreCode: string | null): void {
    if (!selectedStoreCode) return;
    const selectedStore = this.stores.find(
      (store) => store.code === selectedStoreCode
    );
    if (!selectedStore) return;
    this.selectedStore = selectedStore;
    this.storeForm.setValue({ code: selectedStore.code });
    this.setMarker({
      lat: selectedStore.lat,
      lng: selectedStore.lng,
    });
  }

  /**
   * Al seleccionar una tienda desde el sidebar.
   * @param params - Evento de cambio de dropdown.
   */
  onSelectStore({ value }: DropdownChangeEvent): void {
    this.setSelectedStore(value);
  }

  /**
   * Establecer marcador en el mapa.
   * @param coords - Coordenadas de la tienda.
   */
  private setMarker(coords: google.maps.LatLngLiteral): void {
    if (this.isMapReady()) this.mapComponent.setMarker(coords);
    else this.pendingMarkerUpdate.set(true);
  }

  showCloseConfirmation(): void {
    if (this.selectedStoreCode === this.codeField.value) {
      this.visible = false;
      return;
    }
    this.confirmationService.confirm({
      message:
        'Parece que tienes cambios sin guardar. ¿Deseas salir de todas formas y perderlos?',
      header: 'Se perderán los cambios',
      acceptIcon: 'none',
      rejectIcon: 'none',
      rejectButtonStyleClass: 'p-button-text',
      acceptLabel: 'Continuar',
      rejectLabel: 'Cancelar',
      accept: () => {
        this.visible = false;
      },
    });
  }

  /**
   * Mostrar confirmación de selección de tienda.
   */
  showSelectStoreConfirmation(): void {
    this.selectStoreSubmit();
    if (this.isShowedSelectStoreObservation()) return;

    this.confirmationService.confirm({
      message:
        'El precio de los productos puede haber variado según la tienda seleccionada.',
      header: 'Observación',
      acceptIcon: 'none',
      rejectIcon: 'none',
      rejectButtonStyleClass: 'p-button-text',
      acceptLabel: 'Continuar',
      rejectVisible: false,
    });
    this.isShowedSelectStoreObservation.set(true);
  }

  /**
   * Al seleccionar una tienda desde el sidebar.
   */
  private selectStoreSubmit(): void {
    this.visible = false;
    if (this.storeForm.invalid || !this.selectedStore) return;
    this.onSubmit.emit(this.selectedStore);
  }
}
